/**
 * Centralized Firestore Store using Zustand
 * 
 * This store manages ALL Firestore listeners in one place to:
 * - Prevent duplicate listeners
 * - Enable proper cleanup
 * - Share data across all components
 * - Maintain cache during route changes
 * - Handle offline/online transitions gracefully
 */

import { create } from 'zustand';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot,
  where
} from 'firebase/firestore';
import { db } from '../firebase/config';

// Active listener registry to prevent duplicates
const activeListeners = new Map();

// Helper to create a unique key for each listener
const getListenerKey = (collectionName, queryParams = {}) => {
  return `${collectionName}_${JSON.stringify(queryParams)}`;
};

const useFirestoreStore = create((set, get) => ({
  // Data state for each collection
  bills: [],
  customers: [],
  inventory: [],
  staff: [],
  users: [],
  expenses: [],
  expenseCategories: [],
  creditors: [],
  returns: [],
  activityLogs: [],
  suppliers: [],
  purchaseOrders: [],
  grns: [],
  stockTransfers: [],
  tradePayables: [],
  tradeReceivables: [],
  
  // Loading states
  loading: {
    bills: true,
    customers: true,
    inventory: true,
    staff: true,
    users: true,
    expenses: true,
    expenseCategories: true,
    creditors: true,
    returns: true,
    activityLogs: true,
    suppliers: true,
    purchaseOrders: true,
    grns: true,
    stockTransfers: true,
    tradePayables: true,
    tradeReceivables: true,
  },
  
  // Error states
  errors: {},
  
  // Listener management
  listeners: {},
  
  /**
   * Subscribe to a Firestore collection
   * Prevents duplicate listeners and handles cleanup
   */
  subscribe: (collectionName, options = {}) => {
    const { 
      orderByField = 'createdAt', 
      orderDirection = 'desc',
      whereConditions = [],
      onData,
      onError
    } = options;
    
    const listenerKey = getListenerKey(collectionName, { orderByField, orderDirection, whereConditions });
    
    // Check if listener already exists
    if (activeListeners.has(listenerKey)) {
      console.log(`✓ Reusing existing listener for ${collectionName}`);
      return activeListeners.get(listenerKey);
    }
    
    console.log(`→ Creating new listener for ${collectionName}`);
    
    try {
      // Build query
      let q = collection(db, collectionName);
      
      // Apply where conditions
      if (whereConditions.length > 0) {
        whereConditions.forEach(([field, operator, value]) => {
          q = query(q, where(field, operator, value));
        });
      }
      
      // Apply ordering
      if (orderByField) {
        q = query(q, orderBy(orderByField, orderDirection));
      }
      
      // Create listener
      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const data = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          // Update store
          set((state) => ({
            [collectionName]: data,
            loading: { ...state.loading, [collectionName]: false },
            errors: { ...state.errors, [collectionName]: null }
          }));
          
          // Call custom callback if provided
          if (onData) {
            onData(data);
          }
          
          console.log(`✓ ${collectionName}: ${data.length} documents loaded`);
        },
        (error) => {
          console.error(`✗ Error in ${collectionName} listener:`, error);
          
          set((state) => ({
            loading: { ...state.loading, [collectionName]: false },
            errors: { ...state.errors, [collectionName]: error.message }
          }));
          
          if (onError) {
            onError(error);
          }
        }
      );
      
      // Store unsubscribe function
      activeListeners.set(listenerKey, unsubscribe);
      
      set((state) => ({
        listeners: { ...state.listeners, [listenerKey]: unsubscribe }
      }));
      
      return unsubscribe;
      
    } catch (error) {
      console.error(`✗ Failed to create listener for ${collectionName}:`, error);
      
      set((state) => ({
        loading: { ...state.loading, [collectionName]: false },
        errors: { ...state.errors, [collectionName]: error.message }
      }));
      
      return null;
    }
  },
  
  /**
   * Unsubscribe from a specific collection
   * Only call this when you truly want to stop listening (e.g., app unmount)
   */
  unsubscribe: (collectionName, queryParams = {}) => {
    const listenerKey = getListenerKey(collectionName, queryParams);
    const unsubscribe = activeListeners.get(listenerKey);
    
    if (unsubscribe) {
      console.log(`✓ Unsubscribing from ${collectionName}`);
      unsubscribe();
      activeListeners.delete(listenerKey);
      
      set((state) => {
        const newListeners = { ...state.listeners };
        delete newListeners[listenerKey];
        return { listeners: newListeners };
      });
    }
  },
  
  /**
   * Unsubscribe from ALL listeners
   * Call this only on app unmount
   */
  unsubscribeAll: () => {
    console.log('✓ Cleaning up all Firestore listeners');
    
    activeListeners.forEach((unsubscribe, key) => {
      unsubscribe();
    });
    
    activeListeners.clear();
    
    set({ listeners: {} });
  },
  
  /**
   * Optimistic update - update UI immediately before Firestore confirms
   */
  optimisticUpdate: (collectionName, id, updates) => {
    set((state) => ({
      [collectionName]: state[collectionName].map(item =>
        item.id === id ? { ...item, ...updates } : item
      )
    }));
  },
  
  /**
   * Optimistic add - add item to UI immediately
   */
  optimisticAdd: (collectionName, item) => {
    set((state) => ({
      [collectionName]: [item, ...state[collectionName]]
    }));
  },
  
  /**
   * Optimistic delete - remove item from UI immediately
   */
  optimisticDelete: (collectionName, id) => {
    set((state) => ({
      [collectionName]: state[collectionName].filter(item => item.id !== id)
    }));
  },
  
  /**
   * Get loading state for a collection
   */
  isLoading: (collectionName) => {
    return get().loading[collectionName] ?? true;
  },
  
  /**
   * Get error for a collection
   */
  getError: (collectionName) => {
    return get().errors[collectionName] ?? null;
  },
  
  /**
   * Check if any collection is loading
   */
  isAnyLoading: () => {
    const loading = get().loading;
    return Object.values(loading).some(isLoading => isLoading);
  }
}));

export default useFirestoreStore;
